import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import { VoucherInitModel, VoucherInitVariables, VoucherModel, VoucherVariables } from '../../../../models/email-template/voucher.interface';

/**
 * Mockup-styled Voucher block - a drop-in replacement for app-voucher in
 * block-renderer-v2 (same [block]/[isPreview] inputs and outputs), so it
 * covers both the center-canvas summary (isPreview=true) and the
 * right-panel edit form (isPreview=false). Reuses the real, unmodified
 * app-block-text (image upload/segment/validation), app-field-editor-v2 and
 * app-pin sub-components and the exact same VoucherVariables field names -
 * only the surrounding layout is new. Two equal columns (image, then a
 * shaded content box with centered heading/text/CTA - matches the mockup's
 * own .cr-voucher-layout), plus a full-width paragraph strip below (the
 * mockup's own .cr-voucher-paragraph-section) - unlike Gift Voucher CTA/Gift
 * Voucher noCTA, which have neither the shaded box nor the extra paragraph.
 */
@Component({
  selector: 'app-voucher-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './voucher-v2.component.html',
  styleUrl: './voucher-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class VoucherComponentV2 extends BaseComponent implements OnInit {
  @Input() block: VoucherModel = new VoucherInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new VoucherInitVariables();
    }
  }

  imagePlaceholder = 'Image';
  imageAltPlaceholder = 'Enter ALT Text';
  headingPlaceholder = 'Enter Voucher Header';
  textPlaceholder = 'Enter Voucher Text';
  linkPlaceholder = 'Enter CTA Link';
  ctaTextPlaceholder = 'Enter CTA Text';
  paragraphPlaceholder = 'Enter Voucher Paragraph';

  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  get ctaPreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['VoucherLink']);
    const segment = this.block.variables?.['VoucherLinkSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof VoucherVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
